<script setup lang="ts">
/**
 * The loading graphic: a Lissajous figure drawn the way an oscilloscope draws
 * one, by a beam.
 *
 * By default the figure is the mark itself (3:2, x = cos 3t, y = sin 2t on its
 * 216-unit grid); others are chosen in Settings > Appearance (see
 * constants/loadingFigures.ts), along with the typeball, which is not a
 * figure at all but a moiré (MoireCanvas). The beam runs the curve at the figure's own
 * pace, slow at the turns and fast through the crossings, and its trail fades
 * like phosphor at the figure's own line width. When the loader first appears
 * the beam is out of phase, and the figure tumbles and settles, like a scope
 * trace being tuned in. A faint copy of the finished figure sits underneath.
 *
 * Colors come from CSS, so each host can theme it:
 *   --beam-trace  the trail (default: the loading ink)
 *   --beam-head   the beam's bright point (default: the accent)
 *   --beam-ghost  the settled figure underneath (default: the trail color)
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import MoireCanvas from './MoireCanvas.vue'
import { figureWidth, resolveLoadingFigure, TYPEBALL, type LoadingFigure, type LoadingFigureSetting } from '../../constants/loadingFigures'

const props = withDefaults(defineProps<{ size?: number; figure?: LoadingFigureSetting }>(), {
  size: 80,
  figure: '3:2',
})

const ghostCanvas = ref<HTMLCanvasElement | null>(null)
const trailCanvas = ref<HTMLCanvasElement | null>(null)
const headCanvas = ref<HTMLCanvasElement | null>(null)
const traceProbe = ref<HTMLElement | null>(null)
const ghostProbe = ref<HTMLElement | null>(null)
const headProbe = ref<HTMLElement | null>(null)

const CENTER = 108
const RADIUS = 82
const LOOP_SECONDS = 2.4 // one full figure
const PHOSPHOR_SECONDS = 0.5 // trail brightness falls by 1/e in this long
const TUNE_SECONDS = 0.7 // the detuned phase settles by 1/e in this long
const TUNE_START = 1.3 // radians out of phase on first appearance
const STEP = 0.012 // radians of t per drawn segment

let raf = 0

// Resolved per appearance, so "Surprise me" can land on the typeball too.
const resolved = ref<LoadingFigure | typeof TYPEBALL>(resolveLoadingFigure(props.figure))
const isTypeball = computed(() => resolved.value === TYPEBALL)
// Fewer, wider spokes at small sizes, so the petals stay legible.
const typeballParams = computed(() => ({
  spokes: Math.round(Math.min(72, Math.max(20, props.size * 0.6))),
  offset: 0.05,
  hub: 0.09,
  speed: 0.3,
}))

function start() {
  cancelAnimationFrame(raf)
  const canvases = [ghostCanvas.value, trailCanvas.value, headCanvas.value]
  const [ghost, trail, head] = canvases.map((c) => c?.getContext('2d') ?? null)
  if (!ghost || !trail || !head || !traceProbe.value || !ghostProbe.value || !headProbe.value) return

  const fig = resolved.value
  if (fig === TYPEBALL) return
  const width = figureWidth(fig)
  const at = (t: number, phase: number) => [CENTER + RADIUS * Math.cos(fig.a * t), CENTER + RADIUS * Math.sin(fig.b * t + phase)]

  const dpr = Math.min(window.devicePixelRatio || 1, 3)
  const px = Math.round(props.size * dpr)
  const scale = px / 216
  for (const [i, ctx] of [ghost, trail, head].entries()) {
    canvases[i]!.width = px // also clears it
    canvases[i]!.height = px
    ctx.setTransform(scale, 0, 0, scale, 0, 0)
    ctx.lineCap = 'round'
  }

  // Resolved once: the probes carry the themed colors as their `color`.
  const traceColor = getComputedStyle(traceProbe.value).color
  const headColor = getComputedStyle(headProbe.value).color

  const strokeCurve = (ctx: CanvasRenderingContext2D, from: number, to: number, phase: number) => {
    let [x0, y0] = at(from, phase)
    for (let s = from + STEP; ; s = Math.min(s + STEP, to)) {
      const [x1, y1] = at(s, phase)
      ctx.lineWidth = width(s)
      ctx.beginPath()
      ctx.moveTo(x0, y0)
      ctx.lineTo(x1, y1)
      ctx.stroke()
      x0 = x1; y0 = y1
      if (s >= to) break
    }
  }

  // The settled figure, drawn once. Its faintness is set in CSS.
  ghost.strokeStyle = getComputedStyle(ghostProbe.value).color
  strokeCurve(ghost, 0, Math.PI * 2, 0)

  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return // the settled figure stays, still

  const begin = performance.now()
  let last = begin
  let t = 0
  let fadeFor = 0

  const frame = (now: number) => {
    const dt = Math.min(0.1, (now - last) / 1000)
    last = now
    const phase = TUNE_START * Math.exp(-(now - begin) / 1000 / TUNE_SECONDS)

    // Phosphor: everything already drawn fades. Canvas alpha is 8-bit, so a
    // fade applied in tiny per-frame steps rounds back up and never reaches
    // zero, which would leave the tuning traces behind for good. Fading in
    // steps of at least 12% keeps any leftover under 2%.
    fadeFor += dt
    const fade = 1 - Math.exp(-fadeFor / PHOSPHOR_SECONDS)
    if (fade >= 0.12) {
      trail.globalCompositeOperation = 'destination-out'
      trail.fillStyle = `rgba(0,0,0,${fade})`
      trail.fillRect(0, 0, 216, 216)
      trail.globalCompositeOperation = 'source-over'
      fadeFor = 0
    }

    // The beam's path since the last frame.
    const next = t + (dt / LOOP_SECONDS) * Math.PI * 2
    trail.strokeStyle = traceColor
    strokeCurve(trail, t, next, phase)
    t = next % (Math.PI * 2)

    // The bright point of the beam, with a soft glow.
    head.clearRect(0, 0, 216, 216)
    const [hx, hy] = at(t, phase)
    head.shadowColor = headColor
    head.shadowBlur = px * 0.17 // device pixels: shadows ignore the transform
    head.fillStyle = headColor
    head.beginPath()
    head.arc(hx, hy, width(t) * 0.62, 0, Math.PI * 2)
    head.fill()

    raf = requestAnimationFrame(frame)
  }
  raf = requestAnimationFrame(frame)
}

onMounted(start)
watch(() => [props.figure, props.size], () => {
  resolved.value = resolveLoadingFigure(props.figure)
  nextTick(start)
})
onBeforeUnmount(() => cancelAnimationFrame(raf))
</script>

<template>
  <span class="beam-loader" :class="{ typeball: isTypeball }" :style="{ width: `${size}px`, height: `${size}px` }" aria-hidden="true">
    <MoireCanvas v-if="isTypeball" class="beam-typeball" pattern="typeball" :params="typeballParams" loop :still="3" />
    <template v-else>
      <canvas ref="ghostCanvas" class="beam-layer beam-ghost" />
      <canvas ref="trailCanvas" class="beam-layer" />
      <canvas ref="headCanvas" class="beam-layer" />
    </template>
    <span ref="traceProbe" class="beam-probe beam-probe--trace" />
    <span ref="ghostProbe" class="beam-probe beam-probe--ghost" />
    <span ref="headProbe" class="beam-probe beam-probe--head" />
  </span>
</template>

<style scoped>
.beam-loader {
  --beam-trace-color: var(--beam-trace, var(--loading-ink, var(--secondary, #1f7a72)));
  position: relative;
  display: inline-block;
  flex: none;
}

.beam-layer {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.beam-ghost { opacity: 0.14; }

/* The typeball: spokes in the trace color, the hub in the beam's. */
.beam-typeball {
  --moire-ground: transparent;
  --moire-line: var(--beam-trace-color);
  --moire-accent: var(--beam-head, var(--accent, #ff6b45));
  border-radius: 50%;
}

.beam-probe { display: none; }
.beam-probe--trace { color: var(--beam-trace-color); }
.beam-probe--ghost { color: var(--beam-ghost, var(--beam-trace-color)); }
.beam-probe--head { color: var(--beam-head, var(--accent, #ff6b45)); }

/* With motion reduced, the loader is simply the finished figure. */
@media (prefers-reduced-motion: reduce) {
  .beam-ghost { opacity: 1; }
}
</style>
